
import device from 'bits/device'
import { ResizeObserver } from 'bits/global'
import { observeSizeChange } from 'bits/resize_observer'
import { Component, Prop, Vue } from 'bits/vue'
import ImageThumbnail from 'library/v4/components/ImageThumbnail.vue'
import { calculatePopoverAnchor } from 'library/v4/components/oz-rich-modal.vue'
import OzBaseButton from 'library/v4/components/OzBaseButton.vue'
import OzIcon from 'library/v4/components/OzIcon.vue'
import OzTextButton, { OzTextButtonColorScheme } from 'library/v4/components/OzTextButton.vue'
import OzWallCard from 'library/v4/components/OzWallCard.vue'
import RelativeTime from 'library/v4/components/relative-time.vue'
import throttle from 'lodash.throttle'
import type { Wall as WallSnakeCase, WallCamelCase as Wall } from 'types'

const GAP_SIZE = 16
const MIN_CARD_WIDTH = 190
const MAX_CARD_WIDTH = 259
const MAX_FLICKER_TIMES = 7

function isValidCardWidth(cardWidth: number): boolean {
  return cardWidth >= MIN_CARD_WIDTH && cardWidth <= MAX_CARD_WIDTH
}

function computeCardWidth(containerWidth: number, numCardsPerRow: number): number {
  const totalMargins = numCardsPerRow * GAP_SIZE
  return (containerWidth - totalMargins) / numCardsPerRow
}

function findValidGridDimensions(containerWidth: number): { cardWidth: number; numCardsPerRow: number | null } {
  let numCardsPerRow = Math.floor(containerWidth / MAX_CARD_WIDTH)
  let cardWidth = MAX_CARD_WIDTH
  while (cardWidth >= MIN_CARD_WIDTH) {
    cardWidth = computeCardWidth(containerWidth, numCardsPerRow)

    if (isValidCardWidth(cardWidth)) {
      return { cardWidth, numCardsPerRow }
    } else {
      numCardsPerRow++
    }
  }

  return { numCardsPerRow: null, cardWidth: MIN_CARD_WIDTH }
}

/**
 * @displayName Wall Card List
 */

@Component({
  components: { OzIcon, OzBaseButton, OzTextButton, ImageThumbnail, RelativeTime, OzWallCard },
})
export default class DashWallCardList extends Vue {
  OzTextButtonColorScheme = OzTextButtonColorScheme
  calculatePopoverAnchor = calculatePopoverAnchor
  containerWidth = 1024
  numCardsPerRowHistory: number[] = []
  resizeObserver: ResizeObserver | null = null

  /**
   * Array of walls to display
   */
  @Prop({ default: [] }) walls!: Wall[] | WallSnakeCase[]
  /**
   * Whether or not to show more button
   */
  @Prop({ required: false, default: true }) xMore!: boolean

  /**
   * The timestamp type to show in the card.
   */
  @Prop({ default: 'updatedAt' }) dateAttribute!: string

  get isTablet(): boolean {
    return device.tablet
  }

  get cardWidth(): number {
    return this.numCardsPerRow ? computeCardWidth(this.containerWidth, this.numCardsPerRow) : MIN_CARD_WIDTH
  }

  get numCardsPerRow(): number | null {
    if (this.numCardsPerRowHistory[0]) {
      const cardWidth = computeCardWidth(this.containerWidth, this.numCardsPerRowHistory[0])
      const isValid = isValidCardWidth(cardWidth)
      if (isValid) return this.numCardsPerRowHistory[0]
    }

    if (this.numCardsPerRowHistory.length > MAX_FLICKER_TIMES) {
      // Use the smaller card size of the latest two.
      const numCards = this.numCardsPerRowHistory
      return Math.max(numCards[0], numCards[1])
    }

    const gridDimensions = findValidGridDimensions(this.containerWidth)
    const computedNumCardsPerRow = gridDimensions.numCardsPerRow
    if (computedNumCardsPerRow) this.numCardsPerRowHistory.unshift(computedNumCardsPerRow)
    return computedNumCardsPerRow
  }

  measureContainerWidth(): void {
    if (this.numCardsPerRowHistory.length > MAX_FLICKER_TIMES) return

    this.$nextTick(() => {
      const containerRef = this.$el
      if (!containerRef) return

      const containerEl = containerRef as HTMLDivElement
      this.containerWidth = containerEl.getBoundingClientRect().width
    })
  }

  mounted(): void {
    this.measureContainerWidth()
    const throttled = throttle(this.measureContainerWidth, 1100, { leading: true })
    this.resizeObserver = observeSizeChange(this.$el as HTMLDivElement, throttled)
  }

  beforeDestroy(): void {
    this.resizeObserver?.unobserve(this.$el)
  }

  handleMoreClick(wall: Wall, e: PointerEvent): void {
    const popoverAnchor = this.calculatePopoverAnchor(e, 'side')
    this.$emit('wall-more', { wall, popoverAnchor })
  }
}
